import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QueryKey } from '@tanstack/react-query';

import { useCurrentUserQuery } from '@/features/users';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

import { DriveFolderRef } from '../model/driveFolders';
import { DriveUploadIntent } from '../model/types';
import {
  DrivePickerError,
  OpenPicker,
  beginDriveSignIn,
  preloadDrivePicker,
  rememberPickerSettings,
  requestDriveToken,
  showUploadPicker,
} from './googlePicker';
import { useOrderDriveMutations } from './useArtworkDriveMutations';

interface PendingUpload {
  folder: DriveFolderRef;
  title?: string;
  intent: DriveUploadIntent;
}

/**
 * "Add files": the backend resolves the folder and ensures the person's own Drive access, then Google's Picker uploads
 * into that folder as their Google account. Google's sign-in window opens only shortly after a click, so sign-in starts
 * in the click itself, beside the server call; when the browser still blocks it, the person is asked for one more click
 * ("Continue with Google"). Once files land, the folder is refreshed so the counts move at once.
 */
export const useDriveUpload = (orderId: string, alsoInvalidate?: QueryKey[]) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: currentUser } = useCurrentUserQuery();
  const { uploadIntent, refresh } = useOrderDriveMutations(orderId, alsoInvalidate);
  const [uploading, setUploading] = useState(false);
  const [pendingSignIn, setPendingSignIn] = useState<PendingUpload>();
  const openPicker = useRef<OpenPicker>();
  const loginHint = currentUser?.email;

  useEffect(() => () => openPicker.current?.close(), []);

  const report = useCallback(
    (error: unknown, pending: PendingUpload | undefined) => {
      const reason = error instanceof DrivePickerError ? error.reason : 'script';
      if (reason === 'popup' && pending) {
        setPendingSignIn(pending);

        return;
      }
      enqueueSnackbar(t(`artwork_drive.upload.failed_${reason.replace('-', '_')}`), {
        variant: 'error',
      });
    },
    [enqueueSnackbar, t],
  );

  const pick = useCallback(
    async ({ folder, title, intent }: PendingUpload, token: string) => {
      const opened = await showUploadPicker({
        folderId: intent.folderId,
        settings: intent,
        token,
        locale: i18n.language,
        title,
      });
      openPicker.current = opened;
      const outcome = await opened.outcome;
      openPicker.current = undefined;
      if (outcome.kind === 'uploaded' && outcome.files.length > 0) {
        enqueueSnackbar(t('artwork_drive.upload.uploaded', { count: outcome.files.length }), {
          variant: 'success',
        });
        refresh.mutate(folder);
      }
    },
    [i18n.language, enqueueSnackbar, t, refresh],
  );

  const upload = useCallback(
    async (folder: DriveFolderRef, title?: string) => {
      setUploading(true);
      const earlySignIn = beginDriveSignIn(loginHint);
      let pending: PendingUpload | undefined;
      try {
        const intent = await uploadIntent.mutateAsync(folder).catch(() => undefined);
        if (!intent) return;
        rememberPickerSettings(intent);
        pending = { folder, title, intent };
        const token = await (earlySignIn ?? requestDriveToken(intent.pickerClientId, loginHint));
        await pick(pending, token);
      } catch (error) {
        report(error, pending);
      } finally {
        setUploading(false);
      }
    },
    [loginHint, uploadIntent, pick, report],
  );

  /** The second click, for a browser that blocked Google's sign-in window after the server call. */
  const continueWithGoogle = useCallback(async () => {
    const pending = pendingSignIn;
    setPendingSignIn(undefined);
    if (!pending) return;
    setUploading(true);
    try {
      const token = await requestDriveToken(pending.intent.pickerClientId, loginHint);
      await pick(pending, token);
    } catch (error) {
      report(error, pending);
    } finally {
      setUploading(false);
    }
  }, [pendingSignIn, loginHint, pick, report]);

  const dismissSignIn = useCallback(() => setPendingSignIn(undefined), []);

  return {
    upload,
    uploading,
    preload: preloadDrivePicker,
    signInNeeded: Boolean(pendingSignIn),
    continueWithGoogle,
    dismissSignIn,
  };
};
